import { competitionTypeNameToLabel } from '~/utils/competition-type';
import { formatPoints } from '~/utils/points';
import { CompetitionTypeNameEnum, LifterResult } from '~/apis/ssf';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { Skeleton } from '~/components/Skeleton/Skeleton';
import { Grid } from '~/components/Grid/Grid';
import { Fragment } from 'react/jsx-runtime';
import { Input } from '~/components/Input';
import { Line } from 'react-chartjs-2';
import { useState } from 'react';
import 'chart.js/auto';

const sortOrder: CompetitionTypeNameEnum[] = ['CPL', 'CBP', 'EPL', 'EBP', 'PBP', 'SPL'];

type Metric = 'weight' | 'points';

function buildChartOptions(metric: Metric) {
  const formatValue = (value: number | string) =>
    metric === 'points' ? formatPoints(typeof value === 'number' ? value : Number(value)) : String(value);

  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        ticks: {
          font: { size: 14 },
          callback: (value: number | string) => formatValue(value),
        },
      },
      x: {
        ticks: {
          font: { size: 14 },
        },
      },
    },
    plugins: {
      tooltip: {
        titleFont: { size: 14 },
        bodyFont: { size: 14 },
        callbacks: {
          label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => {
            const label = ctx.dataset.label ?? '';
            const y = ctx.parsed.y;
            if (y === null) return label;
            return `${label}: ${formatValue(y)}`;
          },
        },
      },
      legend: {
        position: 'bottom' as const,
        align: 'start' as const,
        labels: {
          font: { size: 14 },
          padding: 20,
        },
      },
    },
  };
}

interface Props {
  results?: LifterResult[];
  isLoading: boolean;
}

export function ResultsChart({ results, isLoading }: Props) {
  const [competitionType, setCompetitionType] = useState<CompetitionTypeNameEnum>();
  const [metric, setMetric] = useState<Metric>('weight');

  if (isLoading) return <Skeleton style={{ height: '400px' }} />;

  if (!results?.length) return false;

  const competitionTypeOptions = [...new Set(results?.map(r => r.competition_type.name))].sort((a, b) => {
    return sortOrder.indexOf(a) - sortOrder.indexOf(b);
  });

  const groupedResults: Partial<Record<CompetitionTypeNameEnum, LifterResult[]>> = {};

  results.forEach(result => {
    const { name } = result.competition_type;

    groupedResults[name] ??= [];

    if (groupedResults[name]) groupedResults[name]?.push(result);
  });

  const currentResults = groupedResults[competitionType ?? competitionTypeOptions[0]]?.reverse();

  const labels = currentResults?.map(r => r.date);
  const datasets =
    metric === 'points'
      ? [
          {
            label: 'Poäng',
            data: currentResults?.map(r => r.points),
            borderColor: '#014389',
            backgroundColor: '#014389',
          },
        ]
      : [
          {
            label: 'Knäböj',
            data: currentResults?.map(r => r.squat),
            borderColor: '#551A8B',
            backgroundColor: '#551A8B',
          },
          {
            label: 'Bänkpress',
            data: currentResults?.map(r => r.benchpress),
            borderColor: '#ffdd00',
            backgroundColor: '#ffdd00',
          },
          {
            label: 'Marklyft',
            data: currentResults?.map(r => r.deadlift),
            borderColor: '#1255CC',
            backgroundColor: '#1255CC',
          },
          {
            label: 'Sammanlagt',
            data: currentResults?.map(r => r.total),
            borderColor: '#014389',
            backgroundColor: '#014389',
          },
        ];
  const data = { labels, datasets };

  return (
    <Fragment>
      <PageLayout.SubHeading>Resultatöversikt</PageLayout.SubHeading>

      <Grid style={{ marginBottom: 'var(--size-4)' }}>
        <Grid.Item cols={{ sm: 9, md: 4, lg: 3 }}>
          <Input.Select
            options={competitionTypeOptions.map(o => [competitionTypeNameToLabel(o), o])}
            onChange={selected => setCompetitionType(selected.target.value as CompetitionTypeNameEnum)}
          />
        </Grid.Item>
        <Grid.Item cols={{ sm: 9, md: 4, lg: 3 }}>
          <Input.Select
            options={[
              ['Vikt (kg)', 'weight'],
              ['Poäng', 'points'],
            ]}
            onChange={selected => setMetric(selected.target.value as Metric)}
          />
        </Grid.Item>
      </Grid>

      <Line options={buildChartOptions(metric)} data={data} style={{ maxHeight: '500px' }} />
    </Fragment>
  );
}
